import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { ChevronUp, ChevronDown } from "lucide-react";

const titleStatusMap = {
  "To call": "urgent",
  "Called today": "positive",
  "Follow-ups": "followup",
  "Not contacted": "new",
};

function AnimatedNumber({ value }) {
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    setDisplay(rounded.get());
    const unsubscribe = rounded.onChange((v) => setDisplay(v));
    return unsubscribe;
  }, [rounded]);

  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration: 0.8,
      ease: "easeOut",
    });
    return controls.stop;
  }, [value, motionValue]);

  return <>{display}</>;
}

const DashboardMetricCard = ({
  title,
  value,
  subtitle,
  suffix,
  barColor,
  delta,
  comparisonLabel,
  invertDelta,
}) => {
  const statusKey = titleStatusMap[title] || "new";
  const accentBarColor = barColor || "hsl(217 91% 55%)";
  const subTextColor = "hsl(150 10% 46%)";

  const numericDelta = typeof delta === "number" ? delta : null;
  const isPositive = numericDelta > 0;
  const isNegative = numericDelta < 0;
  const isGood = invertDelta ? isNegative : isPositive;
  const isBad = invertDelta ? isPositive : isNegative;
  const deltaColor = isGood ? "hsl(142 71% 45%)" : isBad ? "hsl(0 72% 51%)" : "hsl(150 10% 46%)";
  const DeltaIcon = isPositive ? ChevronUp : isNegative ? ChevronDown : null;

  return (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      elevation={0}
      sx={{
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        "&:hover": { boxShadow: "0 1px 1px rgba(16,24,40,0.12)" },
      }}
    >
      <Box sx={{ height: 4, width: "100%", backgroundColor: accentBarColor }} />
      <CardContent sx={{ p: "1.25rem" }}>
        <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", letterSpacing: 0.8, textTransform: "uppercase" }}>
          {title}
        </Typography>

        <Box sx={{ mt: 1.5, height: 40 }}>
          <Typography
            sx={{
              fontSize: 30,
              lineHeight: 1,
              fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              color: "hsl(150 30% 12%)",
              letterSpacing: -0.3,
            }}
          >
            <AnimatedNumber value={value} />{suffix || ""}
          </Typography>
        </Box>

        {numericDelta !== null && comparisonLabel ? (
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
            {DeltaIcon && <DeltaIcon size={12} color={deltaColor} />}
            <Typography component="span" sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: deltaColor }}>
              {numericDelta > 0 ? "+" : ""}{numericDelta}%
            </Typography>
            <Typography component="span" sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", ml: 0.5 }}>
              {comparisonLabel}
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              mt: 1,
              color: subTextColor,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardMetricCard;
